import React, { Component } from 'react';

// Helper
import { apiFetch } from '../../Helper/Api';
import { usd, egp } from './format';

// Components
import { Slider } from 'primereact/slider';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Confirm } from '../../components/UI/Overlays';

// The teacher builds their own package: months, storage, students, videos. Only the total is shown (and what one
// month costs, since that is what auto-renew deducts) -- never a price per item. The server computes every quote.
const ROWS = [
    { key: 'months', label: 'عدد الشهور', unit: 'شهر', icon: 'pi-calendar', sliderMax: null, max: 60, min: null },
    { key: 'storageGb', label: 'المساحة', unit: 'جيجا', icon: 'pi-cloud', sliderMax: 500, max: 100000, min: 1,
        hint: 'ساعة فيديو بجودة عادية حوالي 0.5 - 1 جيجا' },
    { key: 'students', label: 'عدد الطلبة المتوقع', unit: 'طالب', icon: 'pi-users', sliderMax: 2000, max: 1000000, min: 0,
        hint: 'الطالب بيتحسب لما تفعّله، ولو زادوا تقدر تطلب زيادة' },
    { key: 'videos', label: 'عدد الفيديوهات', unit: 'فيديو', icon: 'pi-video', sliderMax: 1000, max: 1000000, min: 0,
        hint: 'كل الفيديوهات الموجودة على المنصة، الجديدة والقديمة' },
];

class Calculator extends Component {
    constructor(props) {
        super(props);
        const c = props.config || {};
        const start = props.initial || {};
        this.state = {
            months: Math.min(Math.max(start.months || 4, c.minMonths || 1), c.maxMonths || 12),
            storageGb: start.storageGb || 10,
            students: start.students != null ? start.students : 100,
            videos: start.videos != null ? start.videos : 50,
            autoRenew: false,
            promoInput: '',
            promoCode: null,      // the code sent with quotes once the teacher pressed "apply"
            quote: null,
            quoting: false,
            error: null,
            subscribing: false,
            confirming: null,     // the open "this will be charged" question, with what it will send
        };
        this.timer = null;
        this.seq = 0;
    }

    componentDidMount() { this.requestQuote(); }

    componentWillUnmount() { clearTimeout(this.timer); }

    limits(row) {
        const c = this.props.config || {};
        if (row.key === 'months') return { min: c.minMonths || 1, max: c.maxMonths || 12, sliderMax: c.maxMonths || 12 };
        return { min: row.min, max: row.max, sliderMax: row.sliderMax };
    }

    set = (key, value) => {
        if (value == null || isNaN(value)) return;
        const row = ROWS.find(r => r.key === key);
        const { min, max } = this.limits(row);
        const v = Math.min(Math.max(Math.round(value), min), max);
        this.setState({ [key]: v }, () => {
            clearTimeout(this.timer);
            this.timer = setTimeout(this.requestQuote, 250);
        });
    }

    requestQuote = () => {
        const { months, storageGb, students, videos, promoCode } = this.state;
        const seq = ++this.seq;
        this.setState({ quoting: true });
        apiFetch('/billing/quote', { method: 'POST', body: { months, storageGb, students, videos, promoCode } })
            .then(quote => { if (seq === this.seq) this.setState({ quote, quoting: false, error: null }); })
            .catch(e => { if (seq === this.seq) this.setState({ quoting: false, error: e.message }); });
    }

    applyPromo = () => {
        const code = this.state.promoInput.trim();
        this.setState({ promoCode: code || null }, this.requestQuote);
    }

    clearPromo = () => this.setState({ promoInput: '', promoCode: null }, this.requestQuote);

    subscribe = () => {
        const { months, storageGb, students, videos, autoRenew, quote } = this.state;
        // only a code the last quote accepted is sent
        const promoCode = quote && quote.promoCode ? quote.promoCode : null;
        const balance = Number(this.props.balanceUsd || 0);
        if (!quote) return;
        if (balance < Number(quote.totalUsd)) {
            this.props.onNeedTopUp(Number(quote.totalUsd) - balance);
            return;
        }
        // Asked on the page's own card rather than a browser dialog. What gets
        // sent is frozen here, at the click -- exactly what the old confirm()
        // sent -- so a quote that lands while the card is open changes nothing.
        this.setState({ confirming: { months, storageGb, students, videos, autoRenew, promoCode, totalUsd: quote.totalUsd } });
    }

    doSubscribe = () => {
        const { months, storageGb, students, videos, autoRenew, promoCode } = this.state.confirming;
        this.setState({ subscribing: true });
        apiFetch('/billing/subscribe', { method: 'POST', body: { months, storageGb, students, videos, autoRenew, promoCode } })
            .then(status => { this.setState({ subscribing: false, confirming: null }); this.props.onSubscribed(status); })
            .catch(e => { this.setState({ subscribing: false, confirming: null }); this.props.onError(e.message); });
    }

    render() {
        const { quote, quoting, error, autoRenew, subscribing, promoInput, promoCode, confirming } = this.state;
        const discounted = quote && Number(quote.discountUsd) > 0;
        const balance = Number(this.props.balanceUsd || 0);
        const short = quote ? Number(quote.totalUsd) - balance : 0;

        return (
            <div className="calc">
                <div className="calc-inputs">
                    {ROWS.map(row => {
                        const { min, max, sliderMax } = this.limits(row);
                        const value = this.state[row.key];
                        return (
                            <div className="calc-row" key={row.key}>
                                <div className="calc-row-head">
                                    <label htmlFor={`calc-${row.key}`}><i className={`pi ${row.icon}`} /> {row.label}</label>
                                    <InputNumber inputId={`calc-${row.key}`} value={value} min={min} max={max} suffix={` ${row.unit}`}
                                        onChange={e => this.set(row.key, e.value)} className="calc-number" />
                                </div>
                                <Slider value={Math.min(value, sliderMax)} min={min} max={sliderMax}
                                    onChange={e => this.set(row.key, e.value)} aria-label={row.label} />
                                {row.hint && <small className="b-muted">{row.hint}</small>}
                            </div>
                        );
                    })}
                    <div className="calc-switch">
                        <InputSwitch inputId="calc-auto" checked={autoRenew} onChange={e => this.setState({ autoRenew: e.value })} />
                        <label htmlFor="calc-auto">جدّد تلقائيًا من المحفظة كل شهر بعد انتهاء المدة</label>
                    </div>
                    <div className="calc-promo">
                        <label htmlFor="calc-promo"><i className="pi pi-tag" /> عندك كود خصم؟</label>
                        <div className="p-inputgroup">
                            <InputText id="calc-promo" value={promoInput} dir="ltr" maxLength={50} placeholder="CODE"
                                onChange={e => this.setState({ promoInput: e.target.value.toUpperCase() })}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); this.applyPromo(); } }} />
                            {promoCode && quote && quote.promoCode
                                ? <Button label="إزالة" className="p-button-outlined" onClick={this.clearPromo} />
                                : <Button label="تطبيق" onClick={this.applyPromo} disabled={!promoInput.trim()} />}
                        </div>
                        {quote && quote.promoError && <small className="b-error">{quote.promoError}</small>}
                        {discounted && <small className="calc-promo-ok">تم تطبيق {quote.promoCode}: خصم {usd(quote.discountUsd)}</small>}
                    </div>
                </div>

                <div className={`calc-total${quoting ? ' busy' : ''}`} aria-live="polite">
                    <span className="calc-total-label">الإجمالي</span>
                    {discounted && <s className="calc-total-before" dir="ltr">{usd(quote.fullTotalUsd)}</s>}
                    <strong className="calc-total-usd" dir="ltr">{quote ? usd(quote.totalUsd) : '—'}</strong>
                    <span className="calc-total-egp">{quote ? `≈ ${egp(quote.totalEgp)}` : ''}</span>
                    {quote && <span className="calc-total-month">يعني {usd(quote.monthlyUsd)} في الشهر لمدة {quote.months} شهر</span>}
                    {error && <p className="b-error">{error}</p>}
                    <Button className="calc-cta" disabled={!quote || subscribing || quoting}
                        label={subscribing ? 'جاري الاشتراك...' : short > 0 ? `اشحن ${usd(short)} واشترك` : 'اشترك الآن'}
                        icon={short > 0 ? 'pi pi-wallet' : 'pi pi-check'} onClick={this.subscribe} />
                    <span className="calc-balance">رصيد محفظتك: <b dir="ltr">{usd(balance)}</b></span>
                    {short > 0 && <small className="calc-note">بعد ما الإدارة توافق على الشحن ارجع هنا واضغط اشترك.</small>}
                </div>

                <Confirm
                    visible={!!confirming}
                    onHide={() => { if (!subscribing) this.setState({ confirming: null }); }}
                    tone="warn"
                    icon="pi pi-check-circle"
                    title="تأكد اشتراكك؟"
                    note={confirming ? `هيتخصم ${usd(confirming.totalUsd)} من محفظتك ويبدأ اشتراكك لمدة ${confirming.months} شهر.` : ''}
                    confirmLabel="أيوه، اشترك"
                    busy={subscribing}
                    busyLabel="جاري الاشتراك..."
                    onConfirm={this.doSubscribe}
                />
            </div>
        );
    }
}

export default Calculator;
